/**
 * product-bundle-picker.js - the choice block on the sweater page
 * (snippets/product-bundle-picker.liquid): 1 sweater, 2 sweaters, or a family
 * set of the same design in other colours or sizes. Every option is a card
 * with a picture of what you get (reference round 08-10-2026: Smooche, Dore &
 * Rose), so the value is visible without reading.
 *
 * What it guarantees:
 * - every sweater keeps its own price; a set is the sum of its pieces, and the
 *   add button shows that total;
 * - the gifts only appear while Cortex reports their tier as active
 *   (window.JapCortex.ready, see assets/jc-cortex.js), per card the tier that
 *   card reaches (2 sweaters: a hat of your choice; 3 or more: a hat and a
 *   lint remover, Thomas 09-10-2026), and only gifts that are in stock here
 *   (JapCortex.giftPlan). Every gift line says what it is worth: the shop's
 *   own price in the visitor's currency. The gifts go into the cart at their
 *   normal price; the Shopify discount behind the tier makes them free. The
 *   block never adds a discount or a price of its own;
 * - the free shipping line uses the threshold Cortex reports (the same one
 *   the checkout charges) and only shows on an option whose total reaches it
 *   while one sweater does not: a line that is true for every option says
 *   nothing. It names free shipping as a fact, without a struck-through rate:
 *   the same sweaters bought without the set ship free too, so the rate is
 *   no saving of the set (CRO 10-10, H5);
 * - "Je bespaart" only counts real price differences: the shop's own price of
 *   the gifts the option gives. No gift, no amount;
 * - "1 sweater" is left entirely to the theme's own product form. For 2
 *   sweaters and the family set this script takes over the submit of the main
 *   form and adds all lines in one /cart/add.js call.
 *
 * Every line it adds carries the property _cortex_bundle ('pair' or
 * 'family'), so orders show which option was chosen; a gift also carries
 * _cortex_upsell 'gift'. The add itself is measured by the Cortex pixel,
 * which already watches every call to /cart/add; BundleAdd adds which option
 * and which gifts came along.
 */
if (!customElements.get('bundle-picker')) {
  const MAX_FAMILY_ROWS = 6;
  const DEFAULT_FAMILY_ROWS = 3;
  const MEDIA_PER_OPTION = { single: 1, pair: 2, family: 3 };

  class BundlePicker extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      // The quick-add drawer copies the product section; a set builder in a
      // drawer opened from the cart is one choice too many. One sweater only.
      if (this.closest('quick-add-drawer')) {
        this.remove();
        return;
      }

      try {
        this.data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
      } catch (error) {
        return;
      }

      this.strings = this.data.strings || {};
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;

      this.addButton = this.form.querySelector('[name="add"]');
      this.family = [this.data.current, ...(this.data.family || [])].filter(Boolean);
      this.mode = 'single';
      this.gifts = null;
      // Shipping: Cortex's threshold and rate for the visitor's country once it
      // answers; until then the fallback the snippet measured in the checkout.
      this.shipping = this.fallbackShipping();
      this.hatVariantId = null;
      this.rows = {
        // The second sweater starts EMPTY (Dore & Rose, 08-10-2026): 84,7% of
        // the orders with the same design twice hold different sizes, so a
        // prefilled copy of the first size is usually the wrong one.
        // ... and it starts in ANOTHER colour of the same design when there is
        // one (Thomas 08-10): the strongest measured pairs are the same design
        // in two colours (Rudolf red + dark blue 20x, see CONVERSIE-THEMA.md).
        pair: [{ product: 0, variantId: null, main: true }, { product: this.data.family && this.data.family.length ? 1 : 0, variantId: null }],
        family: Array.from({ length: DEFAULT_FAMILY_ROWS }, (unused, index) => ({
          product: 0,
          variantId: null,
          main: index === 0
        }))
      };

      if (this.data.modelCatalog?.length && window.JapBundleModelPicker) {
        this.modelPicker = new window.JapBundleModelPicker(this);
      }

      this.querySelectorAll('.bundle-option__radio').forEach((radio) => {
        radio.addEventListener('change', () => this.setMode(radio.value));
      });

      // The variant picker rewrites the button text on every size change and
      // only then dispatches this event, so re-rendering here wins. A chosen
      // size also ends "Kies eerst je maat".
      const section = this.closest('.js-product') || document;
      section.addEventListener('on:variant:change', () => {
        if (this.mainVariantId()) this.sayChooseSize(false);
        this.render();
      });

      // The quantity field only matters for one sweater.
      this.form.addEventListener('change', () => this.render());

      // Capture on document runs before product-form.js's own submit listener.
      this.handleSubmit = this.handleSubmit.bind(this);
      document.addEventListener('submit', this.handleSubmit, true);

      if (window.JapCortex) {
        window.JapCortex.ready.then((state) => {
          this.gifts = state && state.gifts ? state.gifts : null;
          // No Cortex answer (proxy down): keep the measured fallback. An answer
          // without shipping for this cart (a country Cortex has no class for,
          // or another currency) promises nothing about shipping.
          this.shipping = state ? state.shipping || null : this.fallbackShipping();
          this.render();
        });
      }

      // After the variant picker's own first update, which runs in a timeout.
      setTimeout(() => this.render());
    }

    disconnectedCallback() {
      this.modelPicker?.close();
      document.removeEventListener('submit', this.handleSubmit, true);
    }

    // ------------------------------------------------------------------ state

    mainVariantId() {
      const input = this.form.querySelector('input[name="id"]');
      return input ? Number(input.value) : null;
    }

    variantOf(row) {
      const variantId = row.main ? this.mainVariantId() : row.variantId;
      const product = this.family[row.product] || this.family[0];
      return product.variants.find((variant) => variant.id === variantId) || null;
    }

    /** How many sweaters an option holds. */
    piecesOf(mode) {
      if (mode === 'pair') return 2;
      if (mode === 'family') return this.rows.family.length;
      return 1;
    }

    /**
     * What an option gives: the gift tier its sweaters reach and the shop can
     * give right now ({ hats, extras, hatSlots }), or null. One sweater gets no
     * gift: the tiers start at two.
     */
    planFor(mode) {
      if (mode === 'single' || !window.JapCortex || !window.JapCortex.giftPlan) return null;
      return window.JapCortex.giftPlan(this.gifts, this.piecesOf(mode), this.data.hats || []);
    }

    /** The hat the visitor picked, when the option's tier offers it; else the first one. */
    chosenHat(plan) {
      if (!plan || !plan.hatSlots) return null;
      return plan.hats.find((hat) => hat.variantId === this.hatVariantId) || plan.hats[0];
    }

    rowsOf(mode) {
      return mode === 'single' ? [] : this.rows[mode];
    }

    currentRows() {
      return this.rowsOf(this.mode);
    }

    /**
     * Total in cents of the sweaters in an option. For a set whose sizes are
     * not all chosen yet it returns the lowest possible total, with
     * `complete: false`, so the card can say "vanaf".
     */
    totalOf(mode) {
      if (this.modelPicker && mode !== 'single') return this.modelPicker.totalOf(mode);
      const main = this.variantOf({ main: true, product: 0 });
      if (mode === 'single') {
        const quantityInput = this.form.querySelector('[name="quantity"]');
        const quantity = Math.max(1, Number(quantityInput ? quantityInput.value : 1) || 1);
        return main ? { cents: main.price * quantity, complete: true } : null;
      }
      if (!main) return null;
      let sum = 0;
      let complete = true;
      for (const row of this.rowsOf(mode)) {
        const variant = this.variantOf(row);
        if (variant && variant.available) {
          sum += variant.price;
        } else {
          complete = false;
          sum += main.price;
        }
      }
      return { cents: sum, complete };
    }

    /** Total in cents of the sweaters chosen so far; null while a size is missing. */
    total() {
      const result = this.totalOf(this.mode);
      return result && result.complete ? result.cents : null;
    }

    /** How many sweaters in the chosen option still have no size. */
    missingSizes() {
      if (this.mode === 'single') return this.variantOf({ main: true, product: 0 }) ? 0 : 1;
      return this.currentRows().filter((row) => {
        const variant = this.variantOf(row);
        return !variant || !variant.available;
      }).length;
    }

    /** The theme's fallback threshold and rate, in euros, so only for a euro cart. */
    fallbackShipping() {
      if (this.dataset.inEuros !== 'true') return null;
      const freeFrom = Number(this.dataset.fallbackFreeFrom);
      const price = Number(this.dataset.fallbackShippingPrice);
      if (!Number.isFinite(freeFrom) || !Number.isFinite(price)) return null;
      return { price, freeFrom };
    }

    /**
     * Whether this option ships free while one sweater does not, the only
     * case in which its free shipping line tells the visitor something. The
     * total of an option without all sizes chosen is the "vanaf" total.
     */
    freeShippingOver(mode) {
      const shipping = this.shipping;
      if (!shipping || shipping.freeFrom === null || shipping.freeFrom === undefined) return false;
      const lowest = this.lowestPrice();
      const pieces = { single: 1, pair: 2, family: this.rows.family.length };
      const totalFor = (which) => {
        const total = this.totalOf(which);
        if (total) return total.cents;
        return lowest === null ? null : lowest * pieces[which];
      };
      const single = totalFor('single');
      const option = totalFor(mode);
      if (single === null || option === null) return false;
      const freeFor = (cents) => shipping.price === 0 || cents >= shipping.freeFrom;
      return freeFor(option) && !freeFor(single);
    }

    /**
     * What the hat is worth, in cents (Thomas 09-10, after the legal check):
     * the price of the hat picked. Before a pick, the one price every hat on
     * offer has; when their prices differ, only "up to" the highest
     * (exact: false), which is never added into "Je bespaart".
     */
    hatWorth(plan) {
      if (!plan || !plan.hatSlots) return { cents: 0, exact: true };
      const picked = plan.hats.find((hat) => hat.variantId === this.hatVariantId);
      if (picked) return { cents: picked.price || 0, exact: true };
      const prices = plan.hats.map((hat) => hat.price || 0);
      return { cents: Math.max(...prices), exact: prices.every((price) => price === prices[0]) };
    }

    /**
     * What the gifts of an option are worth, in cents: the hat plus every
     * fixed gift. null while the hat has no exact worth yet (no pick, prices
     * differ): then the card makes no savings claim.
     */
    giftValueFor(mode) {
      const plan = this.planFor(mode);
      if (!plan) return 0;
      const hat = this.hatWorth(plan);
      if (!hat.exact) return null;
      return hat.cents + plan.extras.reduce((sum, product) => sum + (product.price || 0), 0);
    }

    /** "ontpluizer" with a capital, for a row label. */
    label(text) {
      const value = String(text || '');
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    // -------------------------------------------------------------- rendering

    money(cents) {
      if (window.JapCortex) return window.JapCortex.formatMoney(cents);
      return (cents / 100).toFixed(2);
    }

    fill(template, values) {
      return String(template || '').replace(/\[(\w+)\]/g, (match, name) =>
        values[name] !== undefined ? values[name] : match
      );
    }

    setMode(mode) {
      this.mode = mode;
      this.querySelectorAll('[data-bundle-option]').forEach((option) => {
        const selected = option.dataset.bundleOption === mode;
        option.classList.toggle('is-selected', selected);
        const body = option.querySelector('[data-bundle-body]');
        if (body) body.hidden = !selected;
      });
      const section = this.closest('.js-product');
      if (section) section.dataset.bundleMode = mode;
      if (mode !== 'single') this.trackOnce('BundleView', { mode });
      this.render();
    }

    render({ preserveRows = false } = {}) {
      // "Van" before the price only while no size is chosen (snippets/price.liquid).
      const main = this.mainVariantId();
      (this.closest('.js-product') || document).querySelectorAll('[data-price-from]').forEach((from) => {
        from.hidden = Boolean(main);
      });
      this.renderGift();
      this.renderShipping();
      this.renderMedia();
      this.renderPrices();
      if (this.mode !== 'single' && !preserveRows) this.renderBody(this.querySelector(`[data-bundle-option="${this.mode}"] [data-bundle-body]`));
      this.renderButton();
      this.renderBarMedia();
    }

    /**
     * The floating buy bar shows what its button adds (Thomas 10-10: "de add
     * to cart knop ... de andere images aan toevoegen, dat je duidelijk ziet
     * wat je allemaal toevoegt"): every sweater of the set, then the free hat
     * picked and any fixed gift, overlapping like the card head. One sweater:
     * the bar keeps its own picture (snippets/standard-buy-bar.liquid).
     */
    renderBarMedia() {
      const slot = document.querySelector('[data-standard-buy-bar] .std-buy-bar__media');
      if (!slot) return;
      if (this.barPicture === undefined) this.barPicture = slot.innerHTML;
      const sources = [];
      if (this.mode !== 'single') {
        this.currentRows().forEach((row) => {
          const image = (this.family[row.product] || this.family[0]).image;
          if (image) sources.push(image);
        });
        const plan = this.planFor(this.mode);
        if (plan && plan.hatSlots) {
          const hat = this.chosenHat(plan);
          if (hat && hat.image) sources.push(hat.image);
        }
        if (plan) plan.extras.forEach((product) => { if (product.image) sources.push(product.image); });
      }
      const key = sources.join('|');
      if (slot.dataset.key === key) return;
      slot.dataset.key = key;
      slot.classList.toggle('std-buy-bar__media--stack', sources.length > 1);
      if (sources.length < 2) {
        slot.innerHTML = this.barPicture;
        return;
      }
      slot.textContent = '';
      sources.forEach((src) => slot.appendChild(this.thumb(src)));
    }

    thumb(src, alt) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = alt || '';
      img.loading = 'lazy';
      img.width = 48;
      img.height = 48;
      return img;
    }

    /** The pictures in the card head: what you get with this option. */
    renderMedia() {
      this.querySelectorAll('[data-bundle-option]').forEach((option) => {
        const slot = option.querySelector('[data-bundle-media]');
        if (!slot) return;
        const mode = option.dataset.bundleOption;
        const rows = mode === 'single'
          ? [{ product: 0, main: true }]
          : this.rowsOf(mode).slice(0, MEDIA_PER_OPTION[mode]);
        const sources = rows.map((row) => (this.family[row.product] || this.family[0]).image).filter(Boolean);
        const key = sources.join('|');
        if (slot.dataset.key === key) return;
        slot.dataset.key = key;
        slot.textContent = '';
        slot.dataset.count = String(sources.length);
        sources.forEach((src) => slot.appendChild(this.thumb(src)));
      });
    }

    /**
     * Per card the gift lines of the tier it reaches: the hat line ("+ gratis
     * kerstmuts naar keuze  t.w.v. <price of the hat>") and one line per fixed
     * gift ("+ gratis ontpluizer  t.w.v. <its price>"), in the card's gift
     * band, with the gift pill on the card's edge. Every worth is the shop's
     * own price in the visitor's currency.
     * Nothing shows without a tier the shop can give: the block would promise
     * a gift the visitor cannot get.
     */
    renderGift() {
      this.querySelectorAll('[data-bundle-option]').forEach((option) => {
        const plan = this.planFor(option.dataset.bundleOption);

        const badge = option.querySelector('.bundle-option__badge[data-bundle-gift]');
        if (badge) {
          const count = plan ? plan.hatSlots + plan.extras.length : 0;
          badge.hidden = count === 0;
          badge.textContent = count > 1 ? this.fill(this.strings.badgeGifts, { count }) : this.strings.badgeGift;
        }

        const hatLine = option.querySelector('li[data-bundle-gift]');
        if (!hatLine) return;
        hatLine.hidden = !(plan && plan.hatSlots);
        if (!hatLine.hidden) this.setGiftLine(hatLine, this.chosenHat(plan).image, null, this.hatWorth(plan));

        // One line per fixed gift, rebuilt only when the tier or a price changes.
        const extras = plan ? plan.extras : [];
        const key = extras.map((product) => `${product.id}:${product.price}`).join('|');
        if (option.dataset.giftExtras === key) return;
        option.dataset.giftExtras = key;
        option.querySelectorAll('[data-bundle-gift-extra]').forEach((line) => line.remove());
        let after = hatLine;
        extras.forEach((product) => {
          const line = hatLine.cloneNode(true);
          line.removeAttribute('data-bundle-gift');
          line.setAttribute('data-bundle-gift-extra', '');
          line.hidden = false;
          const name = window.JapCortex.giftName(product);
          this.setGiftLine(line, product.image, this.fill(this.strings.giftExtraLine, { name }), { cents: product.price, exact: true });
          after.after(line);
          after = line;
        });
      });
    }

    /** Fills one gift line: picture, text (null keeps the hat text) and its worth ({ cents, exact }). */
    setGiftLine(line, image, text, worth) {
      const media = line.querySelector('[data-bundle-gift-media]');
      if (media && media.dataset.key !== String(image)) {
        media.dataset.key = String(image);
        media.textContent = '';
        if (image) media.appendChild(this.thumb(image));
      }
      if (text !== null) {
        const label = line.querySelector('[data-bundle-gift-text]');
        if (label) label.textContent = text;
      }
      // The shop's price struck through: the gift costs nothing (Thomas
      // 10-10: "de prijs van de hat ook doorstrepen, en
      // worth weghalen"). Only an exact price: while the hats differ and none
      // is picked, no price at all.
      const value = line.querySelector('[data-bundle-gift-value]');
      if (value) {
        value.textContent = worth.exact && worth.cents > 0 ? this.money(worth.cents) : '';
        value.hidden = value.textContent === '';
      }
    }

    renderShipping() {
      this.querySelectorAll('[data-bundle-option]').forEach((option) => {
        const mode = option.dataset.bundleOption;
        // The line only says what this card gives on top of one sweater: when
        // every card ships free, the delivery line under the button already
        // says so and three repeats would only push the button down.
        const line = option.querySelector('[data-bundle-shipping]');
        if (line) line.hidden = !this.freeShippingOver(mode);
        // "Je bespaart ...": the shop's own price of the gifts, the only part
        // of a set that costs less than buying the same pieces one by one.
        // Shipping is no part of it: any order from the threshold ships free,
        // set or not (CRO 10-10, H5: "€22,95" counted €6,00 shipping).
        const savings = option.querySelector('[data-bundle-savings]');
        if (savings) {
          const gift = this.giftValueFor(mode);
          const total = gift !== null && gift > 0 ? gift : 0;
          savings.hidden = total <= 0;
          const text = savings.querySelector('[data-bundle-savings-text]');
          if (text) text.textContent = total > 0 ? this.fill(this.strings.savings, { amount: this.money(total) }) : '';
        }
      });
    }

    /** Lowest price of a size on sale, in cents: the "vanaf" before a size is chosen. */
    lowestPrice() {
      const prices = this.family[0].variants.filter((variant) => variant.available).map((variant) => variant.price);
      return prices.length ? Math.min(...prices) : null;
    }

    renderPrices() {
      const main = this.variantOf({ main: true, product: 0 });
      const pieces = { single: 1, pair: 2, family: DEFAULT_FAMILY_ROWS };
      const lowest = this.lowestPrice();

      this.querySelectorAll('[data-bundle-option]').forEach((option) => {
        const mode = option.dataset.bundleOption;
        const price = option.querySelector('[data-bundle-price]');
        const sub = option.querySelector('[data-bundle-per-piece]');
        if (!price) return;

        // No size chosen yet: "vanaf" the lowest size price times the pieces,
        // so every card already shows an honest order of magnitude.
        if (!main && (!this.modelPicker || mode === 'single')) {
          const count = mode === 'family' ? this.rows.family.length : pieces[mode];
          price.textContent = lowest === null ? '' : this.fill(this.strings.fromTotal, { price: this.money(lowest * count) });
          if (sub) sub.textContent = '';
          return;
        }

        const total = this.totalOf(mode);
        if (!total) return;
        if (mode === 'single') {
          price.textContent = this.money(total.cents);
          if (sub) sub.textContent = '';
        } else {
          price.textContent = total.complete
            ? this.money(total.cents)
            : this.fill(this.strings.fromTotal, { price: this.money(total.cents) });
          if (sub) sub.textContent = this.modelPicker ? '' : this.fill(this.strings.perPiece, { price: this.money(main.price) });
        }
      });
    }

    renderButton() {
      if (!this.addButton) return;
      const main = this.variantOf({ main: true, product: 0 });
      const base = this.addButton.dataset.addToCartText || this.addButton.textContent.trim();
      // No size chosen yet: the theme's form says "Kies je maat"; keep the
      // button tappable so it can point at the sizes (handleSubmit).
      if (!main) {
        this.addButton.disabled = false;
        this.addButton.classList.add('btn--needs-size');
        return;
      }
      if (!main.available) return; // the variant picker shows "sold out"

      const sum = this.total();
      if (sum === null) {
        // Say what is still missing (Sweenly's "5 MORE SHADE(S)"), and keep
        // the button alive: a tap points at the open size instead of nothing.
        const missing = this.missingSizes();
        this.addButton.textContent = this.fill(missing === 1 ? this.strings.chooseSizesOne : this.strings.chooseSizesOther, { count: missing });
        this.addButton.disabled = false;
        this.addButton.classList.add('btn--needs-size');
        return;
      }
      this.addButton.classList.remove('btn--needs-size');
      this.addButton.disabled = false;
      this.addButton.textContent = this.fill(this.strings.button, { label: base, price: this.money(sum) });
    }

    renderBody(body) {
      if (!body) return;
      const rows = this.currentRows();
      const showColors = this.family.length > 1;
      body.textContent = '';

      if (this.modelPicker) {
        body.appendChild(this.modelPicker.el('p', 'bundle-model-intro', this.data.modelStrings.sub));
      }

      const list = document.createElement('ol');
      list.className = 'bundle-rows';
      rows.forEach((row, index) => list.appendChild(this.renderRow(row, index, showColors)));
      body.appendChild(list);

      if (this.mode === 'family' && rows.length < MAX_FAMILY_ROWS) {
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'bundle-rows__add link';
        add.textContent = this.strings.addPerson;
        add.addEventListener('click', () => {
          rows.push({ product: 0, variantId: null });
          this.render();
        });
        body.appendChild(add);
      }

      const plan = this.planFor(this.mode);
      if (plan && plan.hatSlots) body.appendChild(this.renderHatRow(plan.hats));
      if (plan) plan.extras.forEach((product) => body.appendChild(this.renderExtraRow(product)));
    }

    /** A fixed gift in the set: its picture, its name and "Gratis", no choice. */
    renderExtraRow(product) {
      const row = document.createElement('div');
      row.className = 'bundle-row bundle-row--gift bundle-row--gift-extra';
      row.dataset.giftProductId = product.id;

      const media = document.createElement('span');
      media.className = 'bundle-row__media';
      if (product.image) media.appendChild(this.thumb(product.image, product.title));
      row.appendChild(media);

      const label = document.createElement('span');
      label.className = 'bundle-row__label';
      label.textContent = this.label(window.JapCortex.giftName(product));
      row.appendChild(label);

      const price = document.createElement('span');
      price.className = 'bundle-row__price bundle-row__price--free';
      price.textContent = this.strings.hatFree;
      row.appendChild(price);
      return row;
    }

    renderRow(row, index, showColors) {
      if (this.modelPicker) return this.modelPicker.renderRow(row, index);
      const item = document.createElement('li');
      item.className = 'bundle-row';

      const product = this.family[row.product] || this.family[0];
      const media = document.createElement('span');
      media.className = 'bundle-row__media';
      if (product.image) media.appendChild(this.thumb(product.image, product.color));
      item.appendChild(media);

      const label = document.createElement('span');
      label.className = 'bundle-row__label';
      label.textContent = this.fill(this.strings.row, { number: index + 1 });
      item.appendChild(label);

      const variant = this.variantOf(row);
      if (row.main) {
        const fixed = document.createElement('span');
        fixed.className = 'bundle-row__fixed text-sm';
        fixed.textContent = this.fill(this.strings.fromAbove, {
          size: variant ? `${product.color}, ${variant.title}` : product.color
        });
        item.appendChild(fixed);
      } else {
        const controls = document.createElement('span');
        controls.className = 'bundle-row__controls';
        if (showColors) controls.appendChild(this.colorSelect(row));
        controls.appendChild(this.sizeSelect(row));
        item.appendChild(controls);
      }

      const price = document.createElement('span');
      price.className = 'bundle-row__price';
      price.textContent = variant ? this.money(variant.price) : '';
      item.appendChild(price);

      if (this.mode === 'family' && index >= 2) {
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bundle-row__remove';
        remove.setAttribute('aria-label', this.strings.removePerson);
        remove.textContent = '×';
        remove.addEventListener('click', () => {
          this.rows.family.splice(index, 1);
          this.render();
        });
        item.appendChild(remove);
      }
      return item;
    }

    colorSelect(row) {
      const select = document.createElement('select');
      select.className = 'select bundle-row__select';
      select.setAttribute('aria-label', this.strings.color);
      this.family.forEach((product, index) => {
        const option = new Option(product.color, String(index), false, index === row.product);
        select.appendChild(option);
      });
      select.addEventListener('change', () => {
        row.product = Number(select.value);
        // Same size in the new colour when it exists and is on sale.
        const previous = this.family.flatMap((product) => product.variants)
          .find((variant) => variant.id === row.variantId);
        const match = previous
          ? this.family[row.product].variants.find((variant) => variant.title === previous.title && variant.available)
          : null;
        row.variantId = match ? match.id : null;
        this.render();
      });
      return select;
    }

    sizeSelect(row) {
      const select = document.createElement('select');
      select.className = 'select bundle-row__select';
      select.dataset.bundleSize = '';
      select.setAttribute('aria-label', this.strings.size);
      if (!row.variantId) select.appendChild(new Option(this.strings.sizePlaceholder, '', true, true));
      const product = this.family[row.product] || this.family[0];
      product.variants.forEach((variant) => {
        const text = variant.available ? variant.title : `${variant.title} - ${this.strings.soldOut}`;
        const option = new Option(text, String(variant.id), false, variant.id === row.variantId);
        option.disabled = !variant.available;
        select.appendChild(option);
      });
      select.addEventListener('change', () => {
        row.variantId = Number(select.value) || null;
        this.render();
      });
      return select;
    }

    /**
     * The free hat: one round picture per hat, no text on the choices (Thomas
     * 10-10: "de hat selection moet beter, geen tekst"); the chosen one gets
     * the red ring of the header circles and its name sits on the label line.
     */
    renderHatRow(hats) {
      if (!hats.some((hat) => hat.variantId === this.hatVariantId)) this.hatVariantId = hats[0].variantId;
      const nameOf = (hat) => hat.shortTitle || hat.title;

      const row = document.createElement('div');
      row.className = 'bundle-row bundle-row--gift';

      const label = document.createElement('span');
      label.className = 'bundle-row__label';
      label.textContent = this.strings.hat;
      const choice = document.createElement('span');
      choice.className = 'bundle-row__choice';
      choice.textContent = nameOf(hats.find((hat) => hat.variantId === this.hatVariantId));
      label.append(' ', choice);
      row.appendChild(label);

      const price = document.createElement('span');
      price.className = 'bundle-row__price bundle-row__price--free';
      price.textContent = this.strings.hatFree;
      row.appendChild(price);

      const chips = document.createElement('div');
      chips.className = 'bundle-hats';
      chips.setAttribute('role', 'radiogroup');
      chips.setAttribute('aria-label', this.strings.hat);
      hats.forEach((hat) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'bundle-hat';
        chip.title = nameOf(hat);
        chip.setAttribute('aria-label', nameOf(hat));
        chip.setAttribute('role', 'radio');
        chip.setAttribute('aria-checked', String(hat.variantId === this.hatVariantId));
        chip.classList.toggle('is-selected', hat.variantId === this.hatVariantId);
        const media = document.createElement('span');
        media.className = 'bundle-hat__media';
        if (hat.image) media.appendChild(this.thumb(hat.image, ''));
        chip.appendChild(media);
        chip.addEventListener('click', () => {
          this.hatVariantId = hat.variantId;
          choice.textContent = nameOf(hat);
          chips.querySelectorAll('.bundle-hat').forEach((other) => {
            const selected = other === chip;
            other.classList.toggle('is-selected', selected);
            other.setAttribute('aria-checked', String(selected));
          });
          // The card's hat line, savings and the bar's pictures follow the hat that was picked.
          this.renderGift();
          this.renderShipping();
          this.renderBarMedia();
        });
        chips.appendChild(chip);
      });
      row.appendChild(chips);
      return row;
    }

    // ----------------------------------------------------------------- adding

    /**
     * A size is missing and the visitor hits the button anyway: do not fail
     * quietly with a grey button (Dore & Rose, 08-10-2026) but take them to
     * the first sweater without a size and let it flash once. Without a size
     * for the first sweater that is the size buttons, ringed in the accent
     * with "Kies eerst je maat" under them until a size is chosen (Thomas
     * 18-10: 12 of ~33 buying sessions tapped the button without a size, 7 of
     * them twice or more); once it has one, the size field of the next
     * sweater in the set (Thomas 10-10: "Als je de eerste trui kiest moet het
     * systeem dit weten en moet verwezen worden naar het andere veld"), which
     * also gets the focus. No size is ever chosen for the visitor.
     */
    pointToSizes() {
      let target = null;
      let field = null;
      if (this.mainVariantId() && this.mode !== 'single') {
        const index = this.currentRows().findIndex((row) => {
          const variant = this.variantOf(row);
          return !row.main && (!variant || !variant.available);
        });
        const item = index >= 0
          ? this.querySelectorAll(`[data-bundle-option="${this.mode}"] .bundle-rows > :is(.bundle-row, .bundle-model-row)`)[index]
          : null;
        field = item ? item.querySelector('[data-bundle-size]') : null;
        target = item;
      }
      if (!target) {
        target = this.sizeOptions() || this.closest('.js-product')?.querySelector('.product-options') || null;
        this.sayChooseSize(true);
      }
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.remove('is-pointed');
      void target.offsetWidth;
      target.classList.add('is-pointed');
      setTimeout(() => target.classList.remove('is-pointed'), 1600);
      if (field) setTimeout(() => field.focus({ preventScroll: true }), 400);
    }

    /** The size buttons of this product: the option group that carries the size chart button. */
    sizeOptions() {
      const product = this.closest('.js-product');
      if (!product) return null;
      return [...product.querySelectorAll('fieldset.option-selector')].find((group) => group.querySelector('[data-modal="size-chart"]')) || null;
    }

    /** "Kies eerst je maat" under the sizes (snippets/product-fit-scale.liquid) and the ring around them. */
    sayChooseSize(shown) {
      const note = this.closest('.js-product')?.querySelector('[data-size-missing]');
      if (note) note.hidden = !shown;
      const sizes = this.sizeOptions();
      if (sizes) sizes.classList.toggle('is-size-missing', shown);
    }

    async handleSubmit(evt) {
      if (evt.target !== this.form) return;
      if (!this.mainVariantId()) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.pointToSizes();
        return;
      }
      if (this.mode === 'single') return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      if (this.busy) return;
      if (this.total() === null) {
        this.pointToSizes();
        return;
      }

      const lines = new Map();
      this.currentRows().forEach((row) => {
        const variant = this.variantOf(row);
        lines.set(variant.id, (lines.get(variant.id) || 0) + 1);
      });
      const items = Array.from(lines, ([id, quantity]) => ({
        id,
        quantity,
        properties: { _cortex_bundle: this.mode }
      }));
      // The gifts of the tier this option reaches: the picked hat and every
      // fixed gift, at their normal price (the Shopify discount makes them free).
      const plan = this.planFor(this.mode);
      const hat = this.chosenHat(plan);
      const gifts = [...(hat ? [hat.variantId] : []), ...(plan ? plan.extras.map((product) => product.variantId) : [])];
      gifts.forEach((id) => {
        items.push({ id, quantity: 1, properties: { _cortex_bundle: this.mode, _cortex_upsell: 'gift' } });
      });

      const cartDrawer = document.querySelector('cart-drawer');
      const sections = ['cart-icon-bubble'];
      if (cartDrawer) sections.push(cartDrawer.closest('.shopify-section').id.replace('shopify-section-', ''));

      this.busy = true;
      this.addButton.classList.add('is-loading');
      const errorBox = this.form.querySelector('.js-form-error');
      if (errorBox) errorBox.hidden = true;

      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || this.strings.error);

        this.track('BundleAdd', {
          mode: this.mode,
          pieces: this.currentRows().length,
          hat: Boolean(hat),
          gifts: gifts.length,
          contentIds: items.map((item) => String(item.id))
        });

        if (theme.settings.afterAtc === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data.sections && data.sections['cart-icon-bubble']) {
          bubble.innerHTML = data.sections['cart-icon-bubble'];
        }
        if (cartDrawer) cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
      } catch (error) {
        if (errorBox) {
          errorBox.textContent = error.message || this.strings.error;
          errorBox.hidden = false;
        }
      } finally {
        this.busy = false;
        this.addButton.classList.remove('is-loading');
      }
    }

    // ------------------------------------------------------------ measurement

    track(name, data) {
      const payload = Object.assign({}, data, { source: 'theme' });
      if (window.NanoPixel) {
        window.NanoPixel.track(name, payload);
      } else {
        window.NanoPixelQueue = window.NanoPixelQueue || [];
        window.NanoPixelQueue.push([name, payload]);
      }
    }

    trackOnce(name, data) {
      this.tracked = this.tracked || new Set();
      const key = `${name}:${data.mode}`;
      if (this.tracked.has(key)) return;
      this.tracked.add(key);
      this.track(name, data);
    }
  }

  customElements.define('bundle-picker', BundlePicker);
}
